/**
 * Settlement document uploads scoped to arbitrary stepId (used for dashboard row uploads).
 */

import { useCallback, useEffect, useState } from 'react';
import DocumentUploadSection from '../../settlement/DocumentUploadSection';
import {
  deleteSettlementDocument,
  listSettlementDocuments,
  toDocumentUploadRow,
  uploadSettlementDocument,
} from '../../../utils/settlementDocuments';

interface RowDocumentPanelProps {
  estateId: string | null;
  phase: string;
  stepId: string;
  readOnly?: boolean;
}

export function RowDocumentPanel({ estateId, phase, stepId, readOnly }: RowDocumentPanelProps) {
  const [docs, setDocs] = useState<
    Array<{ id: string; name: string; type: string; size: number; uploadedAt: string; uploadedBy?: string }>
  >([]);

  const refresh = useCallback(async () => {
    if (!estateId) {
      setDocs([]);
      return;
    }
    const rows = await listSettlementDocuments(estateId, phase, stepId);
    setDocs(rows.map(toDocumentUploadRow));
  }, [estateId, phase, stepId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onUpload = useCallback(
    async (file: File) => {
      if (!estateId || readOnly) return;
      const uploaded = await uploadSettlementDocument({ estateId, phase, stepId, file });
      setDocs((prev) => [...prev, toDocumentUploadRow(uploaded)]);
    },
    [estateId, phase, stepId, readOnly]
  );

  const onDelete = useCallback(
    async (docId: string) => {
      if (!estateId || readOnly) return;
      await deleteSettlementDocument(estateId, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    },
    [estateId, readOnly]
  );

  return (
    <DocumentUploadSection
      documents={docs}
      onUpload={onUpload}
      onDelete={estateId && !readOnly ? onDelete : undefined}
      disabled={!estateId || readOnly}
      className="mt-3"
    />
  );
}
